/**
 * Quill 에디터 래퍼 – admin/comm 공용.
 * 이미지: 툴바 버튼 / 붙여넣기 / 드롭 시 uploadUrl로 업로드 후 URL 삽입 (data: base64 방지).
 * 레이아웃에서 Quill CSS/JS 로드 필요 (lib/quill).
 */
import { Modal } from "../utils/Modal";

declare global {
  interface Window {
    Quill?: new (el: HTMLElement, options?: QuillOptions) => QuillInstance;
  }
}

interface QuillOptions {
  theme?: string;
  placeholder?: string;
  modules?: {
    toolbar?: unknown[];
  };
}

interface QuillInstance {
  root: HTMLElement;
  getSelection: (focus?: boolean) => { index: number } | null;
  getLength: () => number;
  insertEmbed: (index: number, type: string, url: string, value?: string) => void;
  setSelection: (index: number) => void;
  getModule: (name: string) => { addHandler?: (name: string, fn: () => void) => void } | null;
}

export interface QuillEditorUploadConfig {
  uploadUrl: string;
  csrf: string;
  /** 폼 필드명: 'file' (comm) 또는 'image' (admin) */
  fileKey?: "file" | "image";
  /** 응답에서 URL 읽기: json.data.file_url 또는 json.data.url 등 */
  getUrl?: (json: Record<string, unknown>) => string | undefined;
}

export interface QuillEditorOptions {
  placeholder?: string;
  toolbar?: unknown[];
  upload?: QuillEditorUploadConfig;
  initialHtml?: string;
}

const DEFAULT_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image"],
  ["clean"],
];

/** 본문 HTML에서 data: base64 이미지 제거 (요청 크기 방지) */
export function stripBase64Images(html: string): string {
  return (html || "").replace(/<img[^>]*src="data:image[^"]*"[^>]*>/gi, "");
}

function getUrlFromResponse(json: Record<string, unknown>): string | undefined {
  const data = json?.data as Record<string, unknown> | undefined;
  if (data?.file_url) return data.file_url as string;
  if (data?.url) return data.url as string;
  if (json?.url) return json.url as string;
  if (json?.location) return json.location as string;
  return undefined;
}

export class QuillEditor {
  private readonly container: HTMLElement;
  private instance: QuillInstance | null = null;
  private readonly opts: QuillEditorOptions;

  constructor(
    container: string | HTMLElement,
    options: QuillEditorOptions = {}
  ) {
    this.container =
      typeof container === "string"
        ? document.querySelector<HTMLElement>(container)!
        : container;
    this.opts = options;
  }

  init(): QuillInstance | null {
    const Quill = typeof window !== "undefined" ? window.Quill : undefined;
    if (!Quill || !this.container) return null;

    this.instance = new Quill(this.container, {
      theme: "snow",
      placeholder: this.opts.placeholder ?? "",
      modules: {
        toolbar: this.opts.toolbar ?? DEFAULT_TOOLBAR,
      },
    });

    if (this.opts.initialHtml) {
      this.instance.root.innerHTML = this.opts.initialHtml;
    }

    const upload = this.opts.upload;
    if (upload?.uploadUrl && this.instance) {
      this.setupImageUpload(upload);
    }

    return this.instance;
  }

  getInstance(): QuillInstance | null {
    return this.instance;
  }

  getHtml(): string {
    return this.instance?.root?.innerHTML ?? "";
  }

  setHtml(html: string): void {
    if (this.instance?.root) this.instance.root.innerHTML = html;
  }

  private setupImageUpload(config: QuillEditorUploadConfig): void {
    const quill = this.instance;
    if (!quill) return;

    const fileKey = config.fileKey ?? "file";
    const getUrl = config.getUrl ?? getUrlFromResponse;

    const uploadImageAndInsert = (file: File, range?: { index: number }): void => {
      if (!file?.type?.startsWith("image/")) return;
      const fd = new FormData();
      fd.append(fileKey, file);
      fd.append("_token", config.csrf);
      if (fileKey === "file") {
        fd.append("category", "image");
        fd.append("target_type", "post");
        fd.append("target_id", "0");
      }
      const index = range?.index ?? (quill.getSelection(true) || { index: quill.getLength() }).index;
      fetch(config.uploadUrl, {
        method: "POST",
        body: fd,
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
      })
        .then((r) => r.json())
        .then((json: Record<string, unknown>) => {
          const url = getUrl(json);
          if (url) {
            quill.insertEmbed(index, "image", url, "user");
            quill.setSelection(index + 1);
          } else {
            void Modal.alert((json.msg as string) || (json.message as string) || "이미지 업로드에 실패했습니다.");
          }
        })
        .catch(() => void Modal.alert("이미지 업로드 중 오류가 발생했습니다."));
    };

    const toolbar = quill.getModule("toolbar");
    if (toolbar?.addHandler) {
      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.style.display = "none";
        document.body.appendChild(input);
        input.addEventListener("change", () => {
          const file = input.files?.[0];
          input.value = "";
          if (file) uploadImageAndInsert(file);
          input.remove();
        });
        input.click();
      });
    }

    this.container.addEventListener("paste", (e: Event) => {
      const ev = e as ClipboardEvent;
      const items = ev.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image/") === 0) {
          ev.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            const range = quill.getSelection(true) || { index: quill.getLength() };
            uploadImageAndInsert(file, range);
          }
          return;
        }
      }
    });

    this.container.addEventListener(
      "drop",
      (e: Event) => {
        const ev = e as DragEvent;
        const files = ev.dataTransfer?.files;
        if (!files?.length) return;
        let file: File | null = null;
        for (let j = 0; j < files.length; j++) {
          if (files[j].type?.indexOf("image/") === 0) {
            file = files[j];
            break;
          }
        }
        if (file) {
          ev.preventDefault();
          ev.stopPropagation();
          const range = quill.getSelection(true) || { index: quill.getLength() };
          uploadImageAndInsert(file, range);
        }
      },
      true
    );
  }
}
